/**
 * Path geometry for transient tutor GESTURES (see `GestureActionUtil`). Renders
 * as a page-anchored SVG rather than a shape, so the stroke is built here
 * instead of being handed to a shape util.
 *
 * WHY NOT rough-notation: the repo already depends on it (`^0.5.1`) and the
 * legacy slide path uses it for exactly this visual, but it anchors to an
 * HTMLElement and positions its SVG in DOCUMENT coordinates — the mark has to
 * live in PAGE coordinates and track the camera. Only the look is borrowed: a
 * jittered multi-pass stroke that overshoots where it started, which is what
 * makes a circle read as drawn rather than placed.
 *
 * Pure and DOM-free on purpose, so it can be unit-tested without a browser.
 */

import type { BoxModel } from "tldraw";
import type { GestureAction } from "../../../shared/schema/AgentActionSchemas";

/** The wire enum is the source of truth; this is the same union, not a copy. */
export type GestureKind = GestureAction["kind"];

export interface GestureGeometry {
  /** Page-space top-left of the SVG frame the path is drawn inside. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Path data, in coordinates local to that frame. */
  d: string;
}

// HOW THE PEN IS FAKED — and nothing about WHERE the mark goes.
//
// The layout numbers (the circle's padding, the underline's gap and overhang)
// are NOT here: the gateway applies them and ships the box this file strokes,
// exactly as it does for the durable `annotate` marks. Their home is
// `gateway/src/canvas-ops/geometry-config.ts` (`GEOMETRY.gesture`), where they
// are env-tunable and sit next to the `annotation.*` values they mirror.

/** Vertical separation between the underline's two passes. */
const UNDERLINE_PASS_OFFSET = 3;
/** Max deviation of a jittered sample from its ideal position, in page px. */
const JITTER = 2.4;
/**
 * Stroke width in SCREEN pixels. `GestureMarks` divides it by the zoom so the
 * mark holds its weight as the camera moves.
 */
export const STROKE_SCREEN_PX = 3;
/**
 * Extra frame around the path. Covers the jitter, the circle's overshoot and
 * half a `STROKE_SCREEN_PX` at the widest zoom we care about — an SVG frame
 * that hugs the path clips the wobble that makes it look hand-drawn. Kept
 * beside the stroke width because it is sized against it.
 */
const FRAME_MARGIN = 8;
/** Samples per pass. Enough for a smooth curve, few enough to keep `d` short. */
const CIRCLE_SAMPLES = 16;
const UNDERLINE_SAMPLES = 7;
/** Passes per mark — two is what reads as "sketched" without looking furry. */
const PASSES = 2;
/** How far past its start point the circling stroke carries, in turns. */
const CIRCLE_OVERSHOOT_TURNS = 0.11;

interface Point {
  x: number;
  y: number;
}

/**
 * mulberry32 — a tiny deterministic PRNG.
 *
 * Determinism is load-bearing, not a testing convenience: the component
 * re-renders whenever the camera zooms (the stroke width is `3 / zoom`), and a
 * fresh `Math.random()` per render would make the mark twitch into a different
 * shape every frame of a pinch.
 */
function makeRandom(seed: number): () => number {
  let a = (seed | 0) + 0x6d2b79f5;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Round to 2dp — shorter `d` strings, and stable values to assert on. */
function r(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Quadratic-through-midpoints smoothing: each sample becomes a control point
 * and the curve passes through the midpoints between them. Cheap, and it turns
 * a jittered polyline into something that looks like a pen stroke.
 */
function smoothPath(points: Point[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${r(points[0].x)} ${r(points[0].y)}`;
  let d = `M ${r(points[0].x)} ${r(points[0].y)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${r(points[i].x)} ${r(points[i].y)} ${r(midX)} ${r(midY)}`;
  }
  const last = points[points.length - 1];
  return `${d} L ${r(last.x)} ${r(last.y)}`;
}

/** Samples for one lap-and-a-bit around a jittered ellipse. */
function circlePass(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  startAngle: number,
  random: () => number,
): Point[] {
  const points: Point[] = [];
  const sweep = Math.PI * 2 * (1 + CIRCLE_OVERSHOOT_TURNS);
  for (let i = 0; i <= CIRCLE_SAMPLES; i++) {
    const t = i / CIRCLE_SAMPLES;
    const angle = startAngle + sweep * t;
    // Taper the jitter to nothing at the very start so the two passes begin
    // together — a split tail reads as two marks rather than one scribble.
    const taper = Math.min(1, t * 6);
    const wobble = (random() - 0.5) * 2 * JITTER * taper;
    points.push({
      x: cx + Math.cos(angle) * (rx + wobble),
      y: cy + Math.sin(angle) * (ry + wobble),
    });
  }
  return points;
}

/** Samples for one nearly-straight jittered run left to right. */
function underlinePass(
  x0: number,
  x1: number,
  y: number,
  random: () => number,
): Point[] {
  const points: Point[] = [];
  for (let i = 0; i <= UNDERLINE_SAMPLES; i++) {
    const t = i / UNDERLINE_SAMPLES;
    // Ends stay put, the middle wanders — a line whose endpoints wobble looks
    // misaligned with the word above it rather than hand-drawn.
    const taper = Math.sin(t * Math.PI);
    points.push({
      x: x0 + (x1 - x0) * t,
      y: y + (random() - 0.5) * 2 * JITTER * 0.5 * taper,
    });
  }
  return points;
}

/**
 * Overlay `PASSES` samplings of the same stroke into one `d`.
 *
 * Each pass gets its own `M`, and the dash sweep therefore draws them in
 * sequence — which is what a second pass over a mark looks like.
 */
function multiPass(samplePass: (pass: number) => Point[]): string {
  return Array.from({ length: PASSES }, (_, p) =>
    smoothPath(samplePass(p)),
  ).join(" ");
}

/**
 * Build the SVG frame and path for one gesture.
 *
 * `box` is in PAGE coordinates. The returned
 * frame is also page-space; `d` is relative to it, so the caller positions an
 * SVGContainer at the frame and draws in local coordinates.
 */
export function gestureGeometry(
  kind: GestureKind,
  box: BoxModel,
  seed: number,
): GestureGeometry {
  const random = makeRandom(seed);

  if (kind === "circle") {
    // `box` IS the mark's box — the gateway already cleared the writing by
    // GEOMETRY.gesture.circlePadding — so the ellipse is inscribed in it.
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const rx = box.w / 2;
    const ry = box.h / 2;
    const left = cx - rx - FRAME_MARGIN;
    const top = cy - ry - FRAME_MARGIN;
    // Start each pass somewhere along the lower-left arc, where a right-handed
    // circling motion tends to begin, with a little variation between passes.
    const baseAngle = Math.PI * 0.75;
    return {
      left,
      top,
      width: rx * 2 + FRAME_MARGIN * 2,
      height: ry * 2 + FRAME_MARGIN * 2,
      d: multiPass(() =>
        circlePass(
          cx - left,
          cy - top,
          rx,
          ry,
          baseAngle + (random() - 0.5) * 0.4,
          random,
        ),
      ),
    };
  }

  // A flat box (h === 0) whose `y` is already the baseline and whose width
  // already includes the overhang — again, applied gateway-side.
  const x0 = box.x;
  const x1 = box.x + box.w;
  const baseY = box.y;
  const left = x0 - FRAME_MARGIN;
  const top = baseY - FRAME_MARGIN;
  return {
    left,
    top,
    width: x1 - x0 + FRAME_MARGIN * 2,
    height: UNDERLINE_PASS_OFFSET * (PASSES - 1) + FRAME_MARGIN * 2,
    d: multiPass((p) =>
      underlinePass(
        x0 - left,
        x1 - left,
        baseY - top + p * UNDERLINE_PASS_OFFSET,
        random,
      ),
    ),
  };
}

/**
 * Fallback when the wire's colour is not a key `useDefaultColorTheme()` can
 * resolve. The field is a bare string on the wire, so an unrecognised value has
 * to degrade to a visible mark rather than an invisible one. Resolved against
 * the live theme in `GestureMarks` — there is no hand-maintained palette list
 * here to drift out of sync with tldraw's.
 */
export const DEFAULT_GESTURE_COLOR = "orange";
